export interface RawFormData {
  card_number: string;
  exp_month: number | string;
  exp_year: number | string;
  card_cvv2: string;
  owner?: string;
  addr_ln1?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface CayanFormData {
  cardnumber: string;
  expirationmonth: string;
  expirationyear: string;
  cvv: string;
  cardholdername?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
}

export interface CayanFormConfig {
  containerId?: string;
  hidden?: boolean;
  cleanup?: boolean;
}

let container: HTMLElement | null = null;
let createdElements: HTMLInputElement[] = [];
const CONTAINER_ID = 'cayan-form-container';

export const loadScript = (src: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      if (window.CayanCheckout) {
        resolve();
      } else {
        setTimeout(() => {
          if (window.CayanCheckout) {
            resolve();
          } else {
            reject(new Error('Script loaded but CayanCheckout not available'));
          }
        }, 1000);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.CayanCheckout) {
        resolve();
        return;
      }
      setTimeout(() => {
        if (window.CayanCheckout) {
          resolve();
        } else {
          console.error(
            'Script loaded but CayanCheckout not initialized after 500ms'
          );
          reject(new Error('Script loaded but CayanCheckout not initialized'));
        }
      }, 500);
    };

    script.onerror = (error) => {
      console.error('Script loading error:', error);
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.appendChild(script);
  });

const getOrCreateContainer = (hidden: boolean = true): HTMLElement => {
  let containerElement = document.getElementById(CONTAINER_ID);

  if (!containerElement) {
    containerElement = document.createElement('div');
    containerElement.id = CONTAINER_ID;
    containerElement.setAttribute('data-cayan-form', 'true');

    if (hidden) {
      containerElement.style.position = 'absolute';
      containerElement.style.left = '-9999px';
      containerElement.style.top = '-9999px';
      containerElement.style.width = '1px';
      containerElement.style.height = '1px';
    }

    document.body.appendChild(containerElement);
  }

  return containerElement;
};

const createOrUpdateElement = (
  id: string,
  name: string,
  value: string
): void => {
  let element = document.getElementById(id) as HTMLInputElement;

  if (!element) {
    element = document.createElement('input');
    element.type = 'text';
    element.id = id;
    element.name = name;
    element.setAttribute('data-cayan', id);
    element.style.position = 'absolute';
    element.style.left = '0';
    element.style.top = '0';
    element.style.width = '1px';
    element.style.height = '1px';
    element.style.opacity = '0';
    element.style.border = 'none';
    element.tabIndex = -1;
    element.setAttribute('autocomplete', 'off');
    element.setAttribute('readonly', 'true');

    if (container) {
      container.appendChild(element);
      createdElements.push(element);
    }
  }

  element.value = value;
};

export const cleanup = (): void => {
  try {
    createdElements.forEach((element) => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });

    if (container && container.children.length === 0) {
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    }

    createdElements = [];
    container = null;
  } catch (error) {
    console.error('Error cleaning up Cayan form elements:', error);
  }
};

export const createFormElements = (
  formData: CayanFormData,
  config: CayanFormConfig = {}
): boolean => {
  try {
    if (config.cleanup) {
      cleanup();
    }

    container = getOrCreateContainer(config.hidden !== false);

    const fieldMappings = [
      { id: 'cardnumber', name: 'cardnumber', value: formData.cardnumber },
      {
        id: 'expirationmonth',
        name: 'expirationmonth',
        value: formData.expirationmonth
      },
      {
        id: 'expirationyear',
        name: 'expirationyear',
        value: formData.expirationyear
      },
      { id: 'cvv', name: 'cvv', value: formData.cvv },
      {
        id: 'cardholdername',
        name: 'cardholdername',
        value: formData.cardholdername || ''
      },
      { id: 'street', name: 'street', value: formData.street || '' },
      { id: 'city', name: 'city', value: formData.city || '' },
      { id: 'state', name: 'state', value: formData.state || '' },
      { id: 'zip', name: 'zip', value: formData.zip || '' }
    ];

    fieldMappings.forEach((field) => {
      createOrUpdateElement(field.id, field.name, field.value);
    });

    return true;
  } catch (error) {
    console.error('Error creating Cayan form elements:', error);
    return false;
  }
};

export const updateFormElements = (
  formData: Partial<CayanFormData>
): boolean => {
  try {
    Object.entries(formData).forEach(([fieldName, value]) => {
      const element = document.getElementById(fieldName) as HTMLInputElement;
      if (element && value !== undefined) {
        element.value = value.toString();
      }
    });

    return true;
  } catch (error) {
    console.error('Error updating Cayan form elements:', error);
    return false;
  }
};

export const validateElements = (): {
  isValid: boolean;
  missingFields: string[];
} => {
  const requiredFields = [
    'cardnumber',
    'expirationmonth',
    'expirationyear',
    'cvv'
  ];
  const missingFields: string[] = [];

  requiredFields.forEach((fieldId) => {
    const element = document.getElementById(fieldId) as HTMLInputElement;
    if (!element || !element.value.trim()) {
      missingFields.push(fieldId);
    }
  });

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

export const getFormValues = (): CayanFormData | null => {
  try {
    const getValue = (id: string): string => {
      const element = document.getElementById(id) as HTMLInputElement;
      return element ? element.value : '';
    };

    return {
      cardnumber: getValue('cardnumber'),
      expirationmonth: getValue('expirationmonth'),
      expirationyear: getValue('expirationyear'),
      cvv: getValue('cvv'),
      cardholdername: getValue('cardholdername'),
      street: getValue('street'),
      city: getValue('city'),
      state: getValue('state'),
      zip: getValue('zip')
    };
  } catch (error) {
    console.error('Error getting form values:', error);
    return null;
  }
};
